<script lang="ts" setup>
    import {computed} from "vue";

    import DLink from "../atoms/DLink.vue";
    import DTag from "../atoms/DTag.vue";
    import DContainer from "../atoms/DContainer.vue";
    import DImg from "../atoms/DImg.vue";
    import DButton from "../atoms/DButton.vue";
    import DText from "../atoms/DText.vue";

    import {formatPriceToUSD} from "@songfinch/utils/src/format/string";
    import {normalizeProductStockTag} from "@songfinch/utils/src/normalizers";

    import type {BaseCardType, ComponentThemes, ProductListItem} from "@songfinch/types";

    const emit = defineEmits(["addButtonClick"]);

    const props = withDefaults(defineProps<BaseCardType & {
        product: ProductListItem,
        theme: ComponentThemes,
        showButton: boolean,
        showAddButton?: boolean
        descriptionLength?: "short" | "extended"
    }>(), {
        theme: "dark",
        showButton: false,
        showAddButton: false,
        descriptionLength: "short",
    });

    const digitalDelievery = computed(() => {
        return ["digital", "streaming"].includes(props.product.product_type);
    });

    const normalizeDescription = (description: string): string => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(description, "text/html");
        const firstElement = doc.body.firstElementChild;
        const text = firstElement?.textContent?.trim() || description;

        return text;
    };


    const stockTag = computed(() => {
        return normalizeProductStockTag(props.product);
    });
</script>

<template>
    <DLink :to="`/products/${product.name}`" :skipVueRouting="showAddButton">
        <DContainer class="product-card">
            <DContainer class="product-card-image-container" :theme="theme">
                <DContainer class="product-head-container">
                    <DTag
                        v-if="product.extra_info.tag"
                        class="product-card-tag"
                        :label="product.extra_info.tag"
                    />

                    <DTag v-if="stockTag" :label="stockTag.label" :tag-color="stockTag.tagColor"/>
                </DContainer>

                <DImg
                    v-if="product.name"
                    class="product-card-image"
                    :src="product?.photo?.url"
                    variant="card-squared"
                    src-fallback="https://media.songfinch.com/static/image_placeholder.png?format=auto&width=720"
                    :alt="product.name"
                />
                <DContainer v-if="showButton" class="product-card-image-utilities">
                    <DButton :data-test-id="`${dataTestId}-button`" :theme="theme" variant="secondary-noir" size="small" label="View Details"/>
                </DContainer>
            </DContainer>

            <DContainer class="product-text">
                <DText variant="p-3-thicc" spacing="m">
                    {{product.title}}
                </DText>
                <DText v-if="product.description" class="product-description" :class="descriptionLength" variant="p-4">
                    {{normalizeDescription(product.description)}}
                </DText>
                <DContainer display="flex">
                    <DText variant="p-2" :class="{'product-price-strikethrough': product?.priceWithDiscount > 0}">
                        {{formatPriceToUSD(product.price)}}
                    </DText>
                    <DText v-if="product.priceWithDiscount" class="product-price-discount" variant="p-2-thicc">
                        {{formatPriceToUSD(product.priceWithDiscount)}}
                    </DText>
                </DContainer>
                <DText v-if="product.priceWithDiscount" variant="p-4" class="product-price-discount-legend">
                    {{product.discountPercent}}% off for {{product.expiresAfter}} minutes
                </DText>
                <DText v-if="digitalDelievery" variant="l-1" color="muted">
                    Digital delivery
                </DText>
            </DContainer>

            <DContainer v-if="showAddButton" class="product-add-button">
                <DButton
                    :data-test-id="`${dataTestId}-button`"
                    :theme="theme"
                    variant="secondary-noir"
                    size="medium"
                    label="Add"
                    @click.prevent="emit('addButtonClick', product)"
                />
            </DContainer>
        </DContainer>
    </DLink>
</template>

<style lang="scss" scoped>
    .product-card {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        max-width: 333px;
        height: 100%;
    }

    .product-card-image-container {
        position: relative;
        max-width: 333px;
        display: grid;
        align-content: flex-start;
        align-items: flex-start;
        border-radius: var(--radius-20);
        overflow: hidden;
    }

    .product-head-container {
        position: absolute;
        top: 1rem;
        left: 1rem;
        z-index: 2;
    }

    .product-card-image {
        z-index: 1;
    }

    .product-card-image-utilities {
        position: absolute;
        bottom: 1rem;
        left: 50%;
        transform: translateX(-50%);
        z-index: 3;
    }

    .product-text {
        margin: 1.25rem 0;
    }

    .product-description {
        display: -webkit-box;
        margin-bottom: .625rem;

        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;

        &.short {
            line-clamp: 2;
            -webkit-line-clamp: 2;
        }

        &.extended {
            line-clamp: 6;
            -webkit-line-clamp: 6;
        }
    }

    .product-price-discount {
        margin-left: .625rem;
    }

    .product-price-discount-legend {
        display: flex;
        text-transform: uppercase;
    }

    .product-price-strikethrough {
        text-decoration: line-through;
    }
</style>
